import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { Row, OverlayTrigger, Tooltip, Card } from 'react-bootstrap';
import { getTranslations, getSroiRatingsParams, getTypologicalAverage } from '@lib/api';
import { Ico21 } from '@icons/new';
import { TooltipInfoIcon } from '@icons';
import { MetricsTable } from 'components/metricsTable/metricsTable';

const SroiDetails = ({
  currentProject,
  monitoredProject,
  sectorCode,
  proponentCode,
  selectedProjectVersion,
}) => {
  const [translations, setTranslations] = useState([]);
  const [sroiParams, setSroiParams] = useState([]);
  const [sroiTypologicalAverage, setSroiTypologicalAverage] = useState('');
  const [monitoringSroiTypologicalAverage, setMonitoringSroiTypologicalAverage] = useState('');

  useEffect(() => {
    getTranslations().then((retrievedTranslation) => {
      setTranslations(retrievedTranslation);
    });

    getSroiRatingsParams().then((retrievedSroiParams) => {
      setSroiParams(retrievedSroiParams);
    });

    if (currentProject.id) {
      getTypologicalAverage(currentProject.id, +selectedProjectVersion - 1).then(
        (retrievedTypologicalAverage) => {
          setSroiTypologicalAverage(
            retrievedTypologicalAverage.code === 200
              ? retrievedTypologicalAverage.response.avgSroiRating.toLocaleString('it-IT', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : 'N/A',
          );
        },
      );
    }
    if (currentProject.id && selectedProjectVersion) {
      getTypologicalAverage(currentProject.id, +selectedProjectVersion).then(
        (retrievedTypologicalAverage) => {
          setMonitoringSroiTypologicalAverage(
            retrievedTypologicalAverage.code === 200
              ? retrievedTypologicalAverage.response.avgSroiRating.toLocaleString('it-IT', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : 'N/A',
          );
        },
      );
    }
  }, [currentProject, currentProject.id, selectedProjectVersion]);

  const sroiDetailRows = useMemo(() => {
    const ratings = currentProject?.sroiratings ?? [];
    const monitoredRatings = monitoredProject?.sroiratings ?? [];
    const t = (code) => translations?.filter((tr) => tr.code === code) ?? [];
    const paramsOfSroi = (code) => sroiParams?.filter((tr) => tr.code === code) ?? [];

    const hasDesc = (code) =>
      paramsOfSroi(code)
        .map((x) => x.description)
        .join('');

    const labelWithOptionalDescTooltip = (code, tooltipId) => (
      <>
        {t(code).map((x) => x.text)}
        {hasDesc(code) && (
          <OverlayTrigger
            placement='right'
            delay={{ show: 200, hide: 400 }}
            overlay={<Tooltip id={tooltipId}>{t(code).map((x) => x.description)}</Tooltip>}>
            <TooltipInfoIcon className='tooltip-ico mb-1' />
          </OverlayTrigger>
        )}
      </>
    );

    const valueNumber0 = (code, monitored) => {
      const item = (monitored ? monitoredRatings : ratings).find((r) => r.code === code);

      return item ? `€ ${item.value.toLocaleString('it-IT', { maximumFractionDigits: 0 })}` : 'N/A';
    };

    const valueDecimalComma = (code, monitored) => {
      const item = (monitored ? monitoredRatings : ratings).find((r) => r.code === code);

      return item ? item.value.toString().replace('.', ',') : 'N/A';
    };

    return [
      // Benefici economici
      {
        id: 'vanBenefits',
        labelNode: labelWithOptionalDescTooltip('VANBENEFITS', 'vanbenefits-tooltip'),
        preMonitoring: valueNumber0('VANBENEFITS', false),
        postMonitoring: valueNumber0('VANBENEFITS', true),
      },

      // Costi economici
      {
        id: 'avgVanCosts',
        labelNode: labelWithOptionalDescTooltip('VANCOSTS', 'vancosts-tooltip'),
        preMonitoring: valueNumber0('VANCOSTS', false),
        postMonitoring: valueNumber0('VANCOSTS', true),
      },

      // VANE
      {
        id: 'vane',
        labelNode: labelWithOptionalDescTooltip('VANE', 'vane-tooltip'),
        preMonitoring: valueNumber0('VANE', false),
        postMonitoring: valueNumber0('VANE', true),
      },

      // SROI medio (TIRE) + tooltip nero + tooltip descrizione se c'è
      {
        id: 'tire',
        labelNode: (
          <>
            {paramsOfSroi('TIRE').map((x) => x.text)}

            {hasDesc('TIRE') && (
              <OverlayTrigger
                placement='right'
                delay={{ show: 200, hide: 400 }}
                overlay={
                  <Tooltip id='tire-tooltip'>
                    {paramsOfSroi('TIRE').map((x) => x.description)}
                  </Tooltip>
                }>
                <TooltipInfoIcon className='tooltip-ico mb-1' />
              </OverlayTrigger>
            )}
          </>
        ),
        preMonitoring: valueDecimalComma('TIRE', false),
        postMonitoring: valueDecimalComma('TIRE', true),
      },

      // Dev. std SROI portfolio (SROI) + tooltip nero
      {
        id: 'sroi',
        labelNode: (
          <span>
            {paramsOfSroi('SROI').map((x) => x.text)}

            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <div
                  className='d-flex p-3 f-500'
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.9)',
                    color: '#ffffff',
                    fontSize: '.9rem',
                    borderRadius: '5px',
                    maxWidth: '450px',
                  }}>
                  <span>{paramsOfSroi('SROI').map((x) => x.description)}</span>
                </div>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          </span>
        ),
        preMonitoring: valueDecimalComma('SROI', false),
        postMonitoring: valueDecimalComma('SROI', true),
      },

      // ICS061 + TOTALCAPEX
      {
        id: 'ics061',
        labelNode: (
          <span>
            {t('ICS061').map((x) => x.text)}

            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <div
                  className='d-flex p-3 f-500'
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.9)',
                    color: '#ffffff',
                    fontSize: '.9rem',
                    borderRadius: '5px',
                  }}>
                  <span className='mr-2'>{t('ICS061').map((x) => x.text)}</span>
                  <span>({t('ICS061').map((x) => x.text)})</span>
                </div>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          </span>
        ),
        preMonitoring: sroiTypologicalAverage,
        postMonitoring: monitoringSroiTypologicalAverage,
      },
    ];
  }, [
    currentProject?.sroiratings,
    monitoredProject?.sroiratings,
    translations,
    sroiTypologicalAverage,
    monitoringSroiTypologicalAverage,
    sroiParams,
  ]);
  const sroiDetailColumns = [
    { key: 'preMonitoring', label: 'Score ex-ante' },
    { key: 'postMonitoring', label: 'Score ex-post' },
  ];
  return (
    <>
      <Card
        className='cards-utente h-95 d-none d-lg-block'
        style={sectorCode === 'EVENTI' ? { marginTop: '4rem' } : null}>
        <Card.Body>
          <Ico21 className='logo-card ico-box-blue' />
          <Card.Title className='margin-title-box text-uppercase pt-3'>
            {translations.filter((trans) => trans.code === 'ICS041').map((trans) => trans.text)}
            <OverlayTrigger
              placement='right'
              delay={{ show: 200, hide: 400 }}
              overlay={
                <Tooltip id='button-tooltip'>
                  {translations
                    .filter((trans) => trans.code === 'ICS041')
                    .map((trans) => trans.description)}
                </Tooltip>
              }>
              <TooltipInfoIcon className='tooltip-ico mb-1' />
            </OverlayTrigger>
          </Card.Title>

          <Row className='d-flex justify-content-center align-items-center h-100'>
            <MetricsTable rowsPerColumn={6} data={sroiDetailRows} columns={sroiDetailColumns} />
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default SroiDetails;
